import React, { useState } from 'react';
import { Upload } from 'lucide-react';
import { api } from '../services/api';
import {
  COLD_START_MESSAGE,
  requestWithColdStartRetry,
} from '../services/coldStartRetry';
import './PDFUploader.css';

const PDFUploader = ({ onUpload }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const handleSelectedFile = (file) => {
    setError('');
    setNotice('');

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const looksLikePdf =
      file.type === 'application/pdf' ||
      String(file.name || '').toLowerCase().endsWith('.pdf');

    if (!looksLikePdf) {
      setSelectedFile(null);
      setError('Selecione um arquivo PDF válido.');
      return;
    }

    setSelectedFile(file);
  };

  const handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    handleSelectedFile(file || null);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!loading) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    if (event.currentTarget === event.target) {
      setIsDragging(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (loading) return;

    const file = event.dataTransfer?.files?.[0];
    handleSelectedFile(file || null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Selecione um arquivo PDF para enviar.');
      return;
    }

    setLoading(true);
    setError('');
    setNotice('');

    try {
      const formData = new FormData();
      formData.append('pdf', selectedFile);

      const response = await requestWithColdStartRetry(
        () =>
          api.post('/api/upload-pdf', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          }),
        {
          maxRetries: 2,
          backoffMs: [800, 2000],
          onRetry: () => setNotice(COLD_START_MESSAGE),
        }
      );

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      onUpload(response.data);
    } catch (err) {
      setNotice('');
      const message = err.response?.data?.error || err.message || 'Erro ao enviar PDF.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pdf-uploader">
      <div
        className={`upload-card ${isDragging ? 'is-dragging' : ''}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="upload-icon">
          <Upload size={56} />
        </div>
        <h2>Envie seu PDF</h2>
        <p>Arraste e solte ou clique para selecionar seu material</p>

        <div className="file-input-wrapper">
          <input
            id="pdf-file"
            className="file-input"
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
          />
          <label className="file-label" htmlFor="pdf-file">
            {selectedFile ? 'Trocar arquivo' : 'Selecionar PDF'}
          </label>
        </div>

        {selectedFile ? (
          <div className="file-selected">
            <strong>Arquivo:</strong> {selectedFile.name}
          </div>
        ) : null}

        <button
          className="upload-button"
          onClick={handleUpload}
          disabled={loading || !selectedFile}
        >
          {loading ? 'Enviando...' : 'Enviar PDF'}
        </button>

        {error && <div className="error-message">{error}</div>}

        {loading && (
          <div className="loading-spinner">
            <div className="spinner" />
            <div>Processando...</div>
            {notice ? <div className="notice-message">{notice}</div> : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFUploader;
